import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../../components/Header/HeaderBar";
import InviteBySMSModal from "../../../components/Modals/InviteBySMSModal";
import NotificationModal from "../../../components/Modals/NotificationModal";
import { useQuery } from "@tanstack/react-query";
import ContentWrapper from "../../../components/ContentWrapper";
import SearchBarWithFilters from "../../../components/Filters/SearchBarWithFilters";
import { auth } from "@mysnowscape/snow-lib";
import Usage from "../Account/Usage";
import { confirmWrapper } from "../../../components/Modals/ConfirmWrapper";
export const AdminEmployeePage = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [displayInviteModal, setDisplayInviteModal] = useState(false);
  const [displayInviteSuccessModal, setDisplayInviteSuccessModal] =
    useState(false);
  const [searchPhrase, setSearchPhrase] = useState("");

  const userQuery = useQuery({
    queryKey: [
      `company/${props.currentUser.currentCompanyId}/users?take=10000`,
    ],
  });
  const companyQuery = useQuery({
    queryKey: [`company/${props.currentUser.currentCompanyId}`],
  });
  const usageQuery = useQuery({
    queryKey: [`company/${props.currentUser.currentCompanyId}/usage`],
  });

  const isActive = (userId) => {
    return companyQuery.isSuccess
      ? companyQuery.data.authorizedUsers[userId]?.active ?? false
      : true; // Default to true until we know
  };

  const filteredUsers = useMemo(() => {
    if (!userQuery.data) return [];
    const phrase = searchPhrase.trim().toLowerCase();
    if (!phrase) return userQuery.data;
    return userQuery.data.filter((user) => {
      const fullName = `${user.firstName ?? ""} ${user.lastName ?? ""}`
        .trim()
        .toLowerCase();
      const phone = user.phoneNumber ?? "";
      return fullName.includes(phrase) || phone.includes(phrase);
    });
  }, [userQuery.data, searchPhrase]);
  return (
    <>
      <HeaderBar
        buttons={[
          {
            color: "blue",
            label: t("common.add"),
            disabled: usageQuery.isLoading,
            onClick: async () => {
              if (
                usageQuery.data.activeUsers >= usageQuery.data.authorizedUsers
              ) {
                if (
                  await confirmWrapper(t("employees.upgradeToAdd"), {
                    okText: t("employees.goToPlanSettings"),
                    description: t("employees.upgradeToAddDescription"),
                  })
                ) {
                  return navigate("/admin/settings/billing");
                }
              } else {
                navigate("/admin/employee/new");
              }
            },
          },
        ]}
        title={t("employees.title")}
      />
      <ContentWrapper loading={userQuery.isLoading}>
        {userQuery.isLoading ? null : (
          <div className="users text-description-gray flex flex-col">
            <div className="px-2">
              <SearchBarWithFilters
                onSearch={setSearchPhrase}
                value={searchPhrase}
                placeholder={t("employees.searchPlaceholder")}
                autoFocus={false}
              />
            </div>
            <div className="grid grid-cols-2 bg-[#eeeeee]  p-2 font-semibold">
              <span>{t("employees.user")}</span>
              <span>{t("employees.access")}</span>
            </div>
            {filteredUsers.map((user) => (
              <div
                className={`user border-b py-2 border-[#e4e4e4] grid grid-cols-2 cursor-pointer ${isActive(user._id) ? null : "opacity-35"}`}
                key={user._id}
                onClick={() => navigate(`/admin/employee/${user._id}`)}
              >
                <div className="flex flex-col">
                  <span className={`font-semibold text-full-black`}>
                    {user.firstName} {user.lastName}{" "}
                    {isActive(user._id) ? null : (
                      <span className="text-description-gray">
                        {t("employees.inactive")}
                      </span>
                    )}
                  </span>
                  <span>{user.phoneNumber}</span>
                </div>
                <div>
                  {auth
                    .getPermissionsForCompany(
                      user,
                      props.currentUser.currentCompanyId,
                    )
                    .map((perm) => (
                      <span
                        key={perm}
                        className="px-2 py-1 bg-[#e9fdf2] border border-[#bfefd1] rounded-full mr-1 text-sm"
                      >
                        {perm}
                      </span>
                    ))}
                </div>
              </div>
            ))}
          </div>
        )}
        {usageQuery.isLoading ? null : (
          <div className="mt-5">
            <Usage
              total={usageQuery.data.authorizedUsers}
              using={usageQuery.data.activeUsers}
              name={t("employees.allowedEmployees")}
              showUpgradeLink={true}
            ></Usage>
          </div>
        )}
      </ContentWrapper>
      <InviteBySMSModal
        adjustForSidebar
        onClose={() => setDisplayInviteModal(false)}
        open={displayInviteModal}
        onSuccess={() => setDisplayInviteSuccessModal(true)}
      />
      <NotificationModal
        adjustForSidebar
        button={{
          color: "blue",
          label: t("common.okay"),
          onClick: () => {
            // Close
            setDisplayInviteSuccessModal(false);
          },
        }}
        open={displayInviteSuccessModal}
        subtitle={t("employees.inviteSent")}
        title={t("common.success")}
      />
    </>
  );
};

const mapStateToProps = (state) => {
  const { currentUser } = state;
  return { currentUser };
};

export default connect(mapStateToProps)(AdminEmployeePage);
